"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.GatewayClient = void 0;
// `node:net` is lazy-loaded inside `connectTcp()` so this module stays
// browser-bundleable. The WebSocket transport doesn't touch it at all;
// the TCP path is Node-only by construction (a browser has no raw TCP
// socket API), and pulling the import to a dynamic site lets Vite /
// Rollup avoid resolving `node:net` for the web build.
const ediabasx_interface_base_1 = require("@emdzej/ediabasx-interface-base");
const DEFAULT_HOST = "127.0.0.1";
const DEFAULT_PORT = 6801;
class GatewayClient extends ediabasx_interface_base_1.EdiabasInterface {
    host;
    port;
    transport;
    url;
    connection;
    nextId = 1;
    pending = new Map();
    connectPromise;
    /**
     * Cached interface identity from the gateway server's underlying
     * iface. Populated lazily on first `connect()` so the synchronous
     * `xtype` / `xvers` BEST2 opcodes (which read `interfaceType` /
     * `interfaceVersion` directly) work without an RPC round-trip in
     * the middle of job execution. Cleared on `disconnect()`.
     *
     * Defaults are empty / 0 — same as the interpreter's xtype/xvers
     * fallback path, so a job that queries identity before `connect()`
     * completes sees the same value as on a no-info backend.
     */
    interfaceType = "";
    interfaceVersion = 0;
    constructor(options = {}) {
        super();
        this.host = options.host ?? DEFAULT_HOST;
        this.port = options.port ?? DEFAULT_PORT;
        this.transport = options.transport ?? "websocket";
        this.url = options.url;
    }
    async connect() {
        await this.ensureConnection();
        const result = await this.request("connect");
        this.connected = Boolean(result?.connected);
        // Eager-fetch the server-side iface identity so the synchronous
        // `xtype` / `xvers` opcodes can read `this.interfaceType` /
        // `this.interfaceVersion` as plain properties — the interpreter
        // can't await a Promise here. We swallow errors: an older
        // gateway that doesn't implement these methods leaves the
        // defaults in place (empty / 0).
        try {
            const [typeResult, versionResult] = await Promise.all([
                this.request("getInterfaceType"),
                this.request("getInterfaceVersion"),
            ]);
            this.interfaceType = typeResult?.value ?? "";
            this.interfaceVersion = versionResult?.value ?? 0;
        }
        catch {
            // Gateway server too old to expose getInterfaceType /
            // getInterfaceVersion — leave defaults untouched.
        }
    }
    async disconnect() {
        if (!this.connection) {
            this.connected = false;
            return;
        }
        try {
            await this.request("disconnect");
        }
        finally {
            try {
                await this.connection.close();
            }
            catch {
                /* already gone */
            }
            this.connection = undefined;
            this.connected = false;
            this.interfaceType = "";
            this.interfaceVersion = 0;
        }
    }
    async send(data) {
        this.assertConnected();
        await this.request("send", { data: Array.from(data) });
    }
    async receive(timeoutMs) {
        this.assertConnected();
        const result = await this.request("receive", {
            timeoutMs
        });
        return Uint8Array.from(result?.data ?? []);
    }
    async sendReceive(data, timeoutMs) {
        this.assertConnected();
        const result = await this.request("sendReceive", {
            data: Array.from(data),
            timeoutMs
        });
        return Uint8Array.from(result?.data ?? []);
    }
    async info() {
        return this.request("info");
    }
    async setParameter(parameter, value) {
        this.assertConnected();
        await this.request("setParam", { parameter, value });
    }
    /**
     * Forward the bulk parameter array used by BEST2's xsetpar opcode. The
     * first element of `parameters` carries a concept ID (KWP / TP2.0 / ISO-TP
     * / ...) that the hardware-side interface dispatches on, so handing the
     * full array across the wire keeps the gateway transparent to whatever
     * session the remote SerialInterface needs to set up.
     */
    async setCommParameter(parameters) {
        this.assertConnected();
        await this.request("setCommParameter", { parameters });
    }
    async setAnswerLength(length) {
        this.assertConnected();
        await this.request("setAnswerLength", { value: length });
    }
    async setRepeatCounter(count) {
        this.assertConnected();
        await this.request("setRepeatCounter", { value: count });
    }
    async transmitData(request) {
        this.assertConnected();
        const result = await this.request("transmitData", {
            data: Array.from(request)
        });
        return Uint8Array.from(result?.data ?? []);
    }
    async getPort(index) {
        this.assertConnected();
        const result = await this.request("getPort", { index });
        return result?.value ?? 0;
    }
    async setPort(index, value) {
        this.assertConnected();
        await this.request("setPort", { index, value });
    }
    get ignitionVoltage() {
        this.assertConnected();
        return this.request("getIgnitionVoltage").then((result) => result?.value ?? 0);
    }
    get batteryVoltage() {
        this.assertConnected();
        return this.request("getBatteryVoltage").then((result) => result?.value ?? 12000);
    }
    get loopTest() {
        this.assertConnected();
        return this.request("getLoopTest").then((result) => result?.value ?? 0);
    }
    async setProgramVoltage(value) {
        this.assertConnected();
        await this.request("setProgramVoltage", { value });
    }
    async rawData(request) {
        this.assertConnected();
        const result = await this.request("rawData", {
            data: Array.from(request)
        });
        return Uint8Array.from(result?.data ?? []);
    }
    async switchSiRelais(time) {
        this.assertConnected();
        await this.request("switchSiRelais", { time });
    }
    // ---- Frequent-mode (xfrequent / keepalive loops) ----
    // Base EdiabasInterface has no-op defaults; we override so the
    // gateway forwards them to whatever backend the server is driving.
    async transmitFrequent(data) {
        this.assertConnected();
        await this.request("transmitFrequent", { data: Array.from(data) });
    }
    async receiveFrequent() {
        this.assertConnected();
        const result = await this.request("receiveFrequent");
        return Uint8Array.from(result?.data ?? []);
    }
    async stopFrequent() {
        this.assertConnected();
        await this.request("stopFrequent");
    }
    // ---- SerialInterface-specific diagnostic accessors ----
    // Not on the abstract base, so we expose them as Promise<number>
    // getters and let the server decide whether the underlying iface
    // supports them (returns 0 on backends that don't).
    get ignitionStatus() {
        this.assertConnected();
        return this.request("getIgnitionStatus").then((r) => r?.value ?? 0);
    }
    get adapterType() {
        this.assertConnected();
        return this.request("getAdapterType").then((r) => r?.value ?? 0);
    }
    get adapterVersion() {
        this.assertConnected();
        return this.request("getAdapterVersion").then((r) => r?.value ?? 0);
    }
    // Note: `interfaceType` / `interfaceVersion` are exposed as the
    // plain properties declared at the top of this class (populated
    // eagerly in `connect()`), not as async functions. BEST2's `xtype`
    // / `xvers` opcodes are synchronous, so a Promise-returning getter
    // would coerce to `"[object Promise]"` when the interpreter writes
    // the string register. The eager-fetch + cache approach matches how
    // SerialInterface / J2534Interface / EnetInterface expose them as
    // sync `readonly` fields.
    async ensureConnection() {
        if (this.connection) {
            return;
        }
        if (this.connectPromise) {
            return this.connectPromise;
        }
        this.connectPromise = (this.transport === "tcp"
            ? this.connectTcp()
            : this.connectWebSocket()).finally(() => {
            this.connectPromise = undefined;
        });
        return this.connectPromise;
    }
    // ---- TCP transport ----
    async connectTcp() {
        // Dynamic import — keeps the top of this module free of Node-only
        // specifiers so the WebSocket-only browser bundle never tries to
        // resolve `node:net`. The TCP path is Node-only anyway: browsers
        // have no raw TCP socket API to back this up.
        const { createConnection } = await import("node:net");
        return new Promise((resolve, reject) => {
            let buffer = "";
            const socket = createConnection(this.port, this.host, () => {
                const conn = {
                    send: (payload) => {
                        if (!socket.destroyed)
                            socket.write(`${payload}\n`);
                    },
                    close: () => new Promise((res) => {
                        if (socket.destroyed) {
                            res();
                            return;
                        }
                        socket.end(() => {
                            socket.destroy();
                            res();
                        });
                    })
                };
                socket.on("data", (chunk) => {
                    buffer += chunk.toString("utf8");
                    let newlineIndex = buffer.indexOf("\n");
                    while (newlineIndex >= 0) {
                        const line = buffer.slice(0, newlineIndex).trim();
                        buffer = buffer.slice(newlineIndex + 1);
                        if (line)
                            this.handleLine(line);
                        newlineIndex = buffer.indexOf("\n");
                    }
                });
                socket.on("error", (error) => this.handleTransportError(error));
                socket.on("close", () => this.handleTransportClose());
                this.connection = conn;
                resolve();
            });
            socket.on("error", (error) => {
                if (!this.connection)
                    reject(error);
            });
        });
    }
    // ---- WebSocket transport ----
    connectWebSocket() {
        const url = this.url ?? `ws://${this.host}:${this.port}`;
        // Use the global `WebSocket` — Node 22+ exposes it as a global, and
        // every browser has it. We don't import `ws` here so the client
        // module stays dep-free for browser bundling.
        const WS = globalThis.WebSocket;
        if (!WS) {
            return Promise.reject(new Error("GatewayClient: WebSocket transport requested but no global WebSocket is available. Node 22+ is required."));
        }
        return new Promise((resolve, reject) => {
            const ws = new WS(url);
            // We receive plain JSON strings; the binary path is unused.
            ws.binaryType = "arraybuffer";
            const conn = {
                send: (payload) => {
                    if (ws.readyState === ws.OPEN)
                        ws.send(payload);
                },
                close: () => new Promise((res) => {
                    if (ws.readyState === ws.CLOSED) {
                        res();
                        return;
                    }
                    const onClose = () => {
                        ws.removeEventListener("close", onClose);
                        res();
                    };
                    ws.addEventListener("close", onClose);
                    try {
                        ws.close();
                    }
                    catch {
                        res();
                    }
                })
            };
            ws.addEventListener("open", () => {
                this.connection = conn;
                resolve();
            });
            ws.addEventListener("message", (event) => {
                // Server is expected to send strings. If we ever get binary
                // (ArrayBuffer), decode as UTF-8 — same payload semantics.
                const data = event.data;
                const text = typeof data === "string"
                    ? data
                    : new TextDecoder().decode(new Uint8Array(data));
                const line = text.trim();
                if (line)
                    this.handleLine(line);
            });
            ws.addEventListener("error", () => {
                // The error event itself carries no detail in browsers; surface
                // a generic message and rely on the close handler for state
                // cleanup.
                if (!this.connection) {
                    reject(new Error("GatewayClient: WebSocket connection failed"));
                }
                else {
                    this.handleTransportError(new Error("WebSocket error"));
                }
            });
            ws.addEventListener("close", () => this.handleTransportClose());
        });
    }
    // ---- Shared JSON-RPC plumbing ----
    handleLine(line) {
        let payload = null;
        try {
            payload = JSON.parse(line);
        }
        catch {
            return;
        }
        if (!payload || payload.id === null || payload.id === undefined) {
            return;
        }
        const pending = this.pending.get(payload.id);
        if (!pending) {
            return;
        }
        this.pending.delete(payload.id);
        if (payload.error) {
            const error = new Error(payload.error.message);
            error.code = payload.error.code;
            error.data = payload.error.data;
            pending.reject(error);
            return;
        }
        pending.resolve(payload.result);
    }
    handleTransportError(error) {
        for (const pending of this.pending.values()) {
            pending.reject(error);
        }
        this.pending.clear();
        this.connected = false;
    }
    handleTransportClose() {
        const error = new Error("Gateway connection closed");
        for (const pending of this.pending.values()) {
            pending.reject(error);
        }
        this.pending.clear();
        this.connected = false;
        this.connection = undefined;
    }
    async request(method, params) {
        await this.ensureConnection();
        const conn = this.connection;
        if (!conn) {
            throw new Error("Gateway connection not available");
        }
        const id = this.nextId++;
        const payload = {
            jsonrpc: "2.0",
            id,
            method,
            params
        };
        return new Promise((resolve, reject) => {
            this.pending.set(id, { resolve: resolve, reject });
            conn.send(JSON.stringify(payload));
        });
    }
    assertConnected() {
        if (!this.connected) {
            throw new Error("Gateway client is not connected");
        }
    }
}
exports.GatewayClient = GatewayClient;
//# sourceMappingURL=gateway-client.js.map